import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';

const CreateInviteModal = ({ isOpen, onClose, onCreate, token }) => {
  const [boards, setBoards] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    projectIds: [],
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchBoards();
      setGeneratedLink('');
    }
  }, [isOpen]);

  const fetchBoards = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boards`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBoards(data.boards || []);
      }
    } catch (error) {
      console.error('Failed to fetch boards:', error);
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invites`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        const inviteUrl = `${window.location.origin}/client-portal/${data.invite.token}`;
        setGeneratedLink(inviteUrl);
        onCreate(data.invite);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create invite');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectToggle = (projectId) => {
    setFormData(prev => ({
      ...prev,
      projectIds: prev.projectIds.includes(projectId)
        ? prev.projectIds.filter(id => id !== projectId)
        : [...prev.projectIds, projectId]
    }));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink);
    alert('Link copied to clipboard!');
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">Create Client Invite</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        
        {generatedLink ? (
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Invite Link Generated!</label>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <input
                  type="text"
                  className="input"
                  value={generatedLink}
                  readOnly
                  data-testid="generated-invite-link"
                />
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={copyToClipboard}
                  data-testid="copy-link-button"
                >
                  Copy
                </button>
              </div>
              <p className="form-helper">Share this link with the client to give them access to the selected projects.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-primary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Client Email *</label>
                <input
                  type="email"
                  className="input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="invite-email-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Select Projects to Share *</label>
                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border-light)', borderRadius: '4px', padding: '8px' }}>
                  {boards.map(board => (
                    <label key={board.id} style={{ display: 'flex', alignItems: 'center', padding: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.projectIds.includes(board.id)}
                        onChange={() => handleProjectToggle(board.id)}
                        style={{ marginRight: '8px' }}
                        data-testid={`project-checkbox-${board.id}`}
                      />
                      <span>{board.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Message (Optional)</label>
                <textarea
                  className="input"
                  rows="3"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Welcome message for the client..."
                  data-testid="invite-message-input"
                />
              </div>
              {error && <div className="form-error">{error}</div>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading || formData.projectIds.length === 0} data-testid="submit-invite">
                {loading ? 'Creating...' : 'Generate Invite Link'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateInviteModal;